import { useEffect, useState } from "react";
import { createFileRoute, Link, useNavigate, useSearch } from "@tanstack/react-router";
import { AuthSplitShell } from "@/components/auth/AuthSplitShell";
import { AuthModeToggle } from "@/components/auth/AuthModeToggle";
import { Eye, EyeOff, Loader2, Mail, Stethoscope, Crown, CheckCircle2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { authClient } from "@/lib/auth/auth-client";

type Plan = "free" | "premium_yearly" | "premium_monthly" | "lifetime" | "lifetime_premium";

// Plain-element recipes matching the reference app's auth screen exactly —
// no shadcn Input/Label/Button wrappers, since those inject their own
// shadow/radius/focus-ring defaults that the reference design doesn't have.
const INPUT_CLS =
  "mt-1 w-full h-10 px-3 rounded-md border border-input bg-background text-[14px] focus:outline-none focus:ring-2 focus:ring-primary/40";
const LABEL_CLS = "text-[12px] font-medium text-foreground";
const PRIMARY_BTN_CLS =
  "h-10 w-full rounded-md bg-primary text-primary-foreground text-[14px] font-medium hover:bg-primary/90 transition-colors inline-flex items-center justify-center gap-2 disabled:opacity-60";
const OAUTH_BTN_CLS =
  "h-10 w-full inline-flex items-center justify-center gap-2 rounded-md border border-border bg-background hover:bg-accent transition-colors text-[13px] font-medium disabled:opacity-60";

// Map external plan keys (used in marketing URLs) → internal new-listing plan keys
const PLAN_MAP: Record<string, "free" | "premium_yearly" | "premium_monthly"> = {
  free: "free",
  premium_yearly: "premium_yearly",
  premium_monthly: "premium_monthly",
  lifetime: "premium_yearly",
  lifetime_premium: "premium_yearly",
};

const PLAN_COPY: Record<string, { title: string; price: string; icon: any }> = {
  free: { title: "Free Listing", price: "$0 · always free", icon: CheckCircle2 },
  premium_yearly: { title: "Premium Yearly", price: "$999/year (was $1,490)", icon: Crown },
  premium_monthly: { title: "Premium Monthly", price: "$99/month (was $149)", icon: Crown },
  lifetime_premium: { title: "Premium Yearly", price: "$999/year (was $1,490)", icon: Crown },
};

export const Route = createFileRoute("/signup")({
  validateSearch: (s: Record<string, unknown>) => ({
    plan: (typeof s.plan === "string" ? s.plan : "free") as Plan,
  }),
  component: SignupPage,
});

function SignupPage() {
  const search = useSearch({ from: "/signup" });
  const navigate = useNavigate();
  const planKey = PLAN_MAP[search.plan] ?? "free";
  const planCopy = PLAN_COPY[planKey];
  const PlanIcon = planCopy.icon;

  const nextUrl = `/dashboard/new-listing?plan=${planKey}`;

  const [mode, setMode] = useState<"signup" | "login">("signup");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [verifySent, setVerifySent] = useState<string | null>(null);
  const [otp, setOtp] = useState("");
  const [signingIn, setSigningIn] = useState(false);

  // Persist plan + redirect intent so OAuth/email-verify round-trips land back here
  useEffect(() => {
    if (typeof window === "undefined") return;
    localStorage.setItem("heallexa_signup_account_type", "doctor");
    localStorage.setItem("heallexa_selected_plan", planKey);
    localStorage.setItem("heallexa_post_login_redirect", nextUrl);
    localStorage.setItem("selected_login_role", "provider");
  }, [planKey, nextUrl]);

  // If already signed in, skip straight to the listing flow with the chosen plan
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await authClient.getSession();
        if (!data?.user || cancelled) return;
        navigate({ to: "/dashboard/new-listing", search: { plan: planKey } as any });
      } catch { /* ignore */ }
    })();
    return () => { cancelled = true; };
  }, [planKey, navigate]);

  const handleGoogle = async () => {
    setSigningIn(true);
    try {
      // Redirects to Google; control returns to /post-login (which honors the
      // stored post_login_redirect → the new-listing flow with the chosen plan).
      await authClient.signIn.social({
        provider: "google",
        callbackURL: window.location.origin + "/post-login",
      });
    } catch {
      setErr("Google sign-in did not start. Please try again.");
      setSigningIn(false);
    }
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) return;
    setBusy(true); setErr(null);
    try {
      if (mode === "signup") {
        if (!fullName.trim()) { setErr("Please enter your full name."); return; }
        const { data, error } = await authClient.signUp.email({ email, password, name: fullName.trim() });
        if (error) { setErr(error.message ?? "Could not create account."); return; }
        // Cognito emailed a 6-digit code → show the verify step instead of navigating.
        if (data?.confirmationRequired) {
          setVerifySent(email);
          toast.success("We sent a 6-digit code to your email.");
          return;
        }
      } else {
        const { error, data } = await authClient.signIn.email({ email, password });
        if (error) { setErr(error.message ?? "Invalid credentials."); return; }
        // MFA challenge in progress → redirecting to /auth/mfa-verify; stop.
        if (data?.twoFactorRedirect) return;
      }
      toast.success("Account ready.");
      // /post-login provisions the provider role (intent stashed above) and
      // honors the stored redirect → the new-listing flow with the chosen plan.
      navigate({ to: "/post-login" });
    } catch {
      setErr("Something went wrong. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onVerifyCode = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!otp || otp.length < 6) { setErr("Enter the 6-digit code."); return; }
    setBusy(true); setErr(null);
    try {
      // Confirm the emailed code, then auto-sign-in with the password in state.
      const { error, data } = await authClient.signUp.confirm({ email, code: otp.trim(), password });
      if (error) { setErr(error.message ?? "Invalid or expired code."); return; }
      if (data?.twoFactorRedirect) return; // redirecting to second-factor screen
      toast.success("Email verified.");
      navigate({ to: "/post-login" });
    } catch {
      setErr("Could not verify code. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const resendCode = async () => {
    setBusy(true); setErr(null);
    try {
      const { error } = await authClient.signUp.resendCode({ email });
      if (error) setErr(error.message);
      else toast.success("New code sent.");
    } finally { setBusy(false); }
  };

  if (verifySent) {
    return (
      <AuthSplitShell>
        <form onSubmit={onVerifyCode} className="space-y-5">
          <div className="space-y-3">
            <div className="h-11 w-11 rounded-md border border-primary/30 bg-primary/10 inline-flex items-center justify-center">
              <Mail className="h-5 w-5 text-primary" />
            </div>
            <h1 className="text-2xl font-semibold tracking-tight">Check your email</h1>
            <p className="text-[14px] text-muted-foreground">
              Enter the 6-digit code we sent to <span className="font-medium text-foreground">{verifySent}</span>.
              After confirming, you'll continue to your {planCopy.title.toLowerCase()} setup.
            </p>
          </div>
          <div>
            <label htmlFor="otp" className={LABEL_CLS}>Verification code</label>
            <input
              id="otp"
              inputMode="numeric"
              autoComplete="one-time-code"
              pattern="[0-9]{6}"
              maxLength={6}
              value={otp}
              onChange={(e) => setOtp(e.target.value.replace(/\D/g, ""))}
              placeholder="123456"
              className={`${INPUT_CLS} text-center tracking-[0.5em] text-lg`}
              required
            />
          </div>
          <button type="submit" className={PRIMARY_BTN_CLS} disabled={busy || otp.length < 6}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <CheckCircle2 className="h-4 w-4" />}
            Verify &amp; continue
          </button>
          <div className="flex items-center justify-between text-xs">
            <button type="button" onClick={resendCode} disabled={busy} className="text-primary hover:underline">Resend code</button>
            <button type="button" onClick={() => { setVerifySent(null); setOtp(""); setErr(null); }} className="text-muted-foreground hover:text-foreground">Use a different email</button>
          </div>
          {err && <p className="text-[13px] text-destructive text-center">{err}</p>}
        </form>
      </AuthSplitShell>
    );
  }

  return (
    <AuthSplitShell>
      <div className="border border-primary/30 bg-primary/5 rounded-md px-4 py-3 mb-6 flex items-center gap-3">
        <PlanIcon className="h-5 w-5 text-primary shrink-0" />
        <div className="flex-1">
          <div className="text-[11px] font-semibold text-primary tracking-[0.18em] uppercase">Selected plan</div>
          <div className="text-sm font-semibold text-foreground">{planCopy.title} <span className="text-muted-foreground font-normal">· {planCopy.price}</span></div>
        </div>
      </div>

      <div className="flex items-center gap-2 text-[11px] font-semibold tracking-[0.18em] uppercase text-muted-foreground mb-2">
        <Stethoscope className="h-4 w-4 text-primary" /> Provider access
      </div>

      <h1 className="text-2xl font-semibold tracking-tight">
        {mode === "signup" ? "Create your provider account" : "Sign in to continue"}
      </h1>
      <p className="mt-1 text-[13px] text-muted-foreground">
        {mode === "signup"
          ? "Takes under a minute. Next you'll add your practice details."
          : "Welcome back. We'll take you to your plan setup."}
      </p>

      <AuthModeToggle mode={mode} onChange={(m) => { setMode(m); setErr(null); }} signInLabel="Log in" signUpLabel="Sign up" />

      <div className="mt-6 space-y-5">
        <button type="button" className={OAUTH_BTN_CLS} onClick={handleGoogle} disabled={signingIn}>
          <svg className="h-4 w-4" viewBox="0 0 24 24">
            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
          </svg>
          {signingIn ? "Continuing…" : `Continue with Google`}
        </button>
        <div className="flex items-center gap-4">
          <div className="h-px flex-1 bg-border" />
          <span className="text-[11px] uppercase tracking-widest text-muted-foreground">or</span>
          <div className="h-px flex-1 bg-border" />
        </div>

        <form onSubmit={submit} className="space-y-4">
          {mode === "signup" && (
            <>
              <div>
                <label htmlFor="name" className={LABEL_CLS}>Full name</label>
                <input id="name" className={INPUT_CLS} value={fullName} onChange={(e) => setFullName(e.target.value)} placeholder="Dr. Jane Smith" required />
              </div>
              <div>
                <label htmlFor="phone" className={LABEL_CLS}>Phone</label>
                <input id="phone" className={INPUT_CLS} type="tel" autoComplete="tel" value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="(555) 123-4567" />
              </div>
            </>
          )}
          <div>
            <label htmlFor="email" className={LABEL_CLS}>Email</label>
            <input id="email" className={INPUT_CLS} type="email" autoComplete="email" placeholder="you@example.com" value={email} onChange={(e) => setEmail(e.target.value)} required />
          </div>
          <div>
            <label htmlFor="password" className={LABEL_CLS}>Password</label>
            <div className="relative">
              <input
                id="password"
                className={`${INPUT_CLS} pr-10`}
                type={showPassword ? "text" : "password"}
                autoComplete={mode === "signup" ? "new-password" : "current-password"}
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                minLength={mode === "signup" ? 8 : undefined}
              />
              <button
                type="button"
                onClick={() => setShowPassword((v) => !v)}
                className="absolute right-0 top-0 mt-1 h-10 w-10 inline-flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors"
                aria-label={showPassword ? "Hide password" : "Show password"}
                tabIndex={-1}
              >
                {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>
          <button type="submit" className={PRIMARY_BTN_CLS} disabled={busy}>
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <PlanIcon className="h-4 w-4" />}
            {mode === "signup" ? `Create account & continue` : `Sign in & continue`}
          </button>
        </form>

        {err && <p className="text-[13px] text-destructive text-center">{err}</p>}
      </div>

      <p className="mt-6 text-center text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">← Back to home</Link>
      </p>
    </AuthSplitShell>
  );
}

